import { isUndefined } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fieldFromComponentData } from '@shared/data/entity/fields/fieldFromComponentData';
import { CardFormType } from '@shared/scenes/entity/cardFormTypeContext';
import { CardEntityError, useFormErrors } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, GetUpdatedField, UpdateField } from '@shared/scenes/entity/types';
import { useViewComponents } from '@shared/scenes/entity/util/useViewComponents';
import { useViewTemplateLayout, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isSubformWorkflowComponent, isTextComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { SupportedSubformComponentValue } from '@shared/types/fieldValues';
import {
  SubformLayoutEntry,
  buildSubformLayoutEntries,
  isSubformComponentEntry,
} from './subformLayoutEntries';
import { useSubformViewTemplate } from './useSubformViewTemplate';

export interface SubformData {
  workflowTemplate: API.WorkflowTemplateFragment | undefined;
  viewTemplate: API.ViewTemplateFragment | undefined;
  components: API.AnyComponent[];
  /** Everything to render, in view template layout order. */
  layoutEntries: SubformLayoutEntry[];
  /** The components of `layoutEntries`, for callers that only care about fields. */
  orderedComponents: API.AnyComponent[];
  fields: FieldRecord;
  updateField: UpdateField;
  errors: CardEntityError[];
  isValid: boolean;
  isInitialized: boolean;
  submitAttempted: boolean;
  setSubmitAttempted: Dispatch<SetStateAction<boolean>>;
}

export function useSubformData({
  workflowTemplateId,
  initialData,
  type,
  apiErrors,
  isReadOnly,
}: {
  workflowTemplateId: string;
  initialData?: Record<string, SupportedSubformComponentValue>;
  type?: CardFormType;
  /** Server-side field-level errors to surface within the inner subform (e.g.
   *  COMPONENT_DATA_REQUIRED errors from the parent form save). Each error's
   *  componentId should match an inner subform component. */
  apiErrors?: CardEntityError[];
  isReadOnly?: boolean;
}): SubformData {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const components = useMemo(
    () => workflowTemplate?.components.filter(isSubformWorkflowComponent) ?? [],
    [workflowTemplate?.components],
  );

  const [fields, setFields] = useState<FieldRecord>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Initialize (and reset) fields whenever workflowTemplateId changes, waiting for the workflow
  // template to finish loading. initialData is captured at that moment.
  //
  // The wait is on the template rather than on `components` being non-empty. A subform may hold
  // no fields at all — one built from instructional content alone — and waiting for a component
  // that was never coming left `isInitialized` false for good, which is the flag the view renders
  // nothing without. The template's arrival is what makes an empty component set an answer rather
  // than a question.
  const lastInitializedId = useRef('');
  useEffect(() => {
    if (
      !workflowTemplateId ||
      workflowTemplateId === lastInitializedId.current ||
      !workflowTemplate
    ) {
      return;
    }

    lastInitializedId.current = workflowTemplateId;
    setFields(initFieldRecord(components, initialData));
    setIsInitialized(true);
    setSubmitAttempted(false);
    // initialData intentionally omitted — we only want to capture it at init time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowTemplateId, workflowTemplate, components]);

  // Reset the init guard when the subform route is no longer active so that
  // re-opening the same template re-initializes from fresh initialData (e.g.
  // after the user removes and re-adds the same subform).
  useEffect(() => {
    if (!workflowTemplateId) {
      lastInitializedId.current = '';
    }
  }, [workflowTemplateId]);

  const viewTemplate = useSubformViewTemplate({
    workflowTemplateId,
    type: type ?? 'EDIT',
  });
  const { layout } = useViewTemplateLayout(workflowTemplateId, viewTemplate?.id);

  // Use the same hook chain as useEditCardEntity: useVisibleComponents (filters isHidden) →
  // applyComponentOverrides (merges required/readonly/isHidden overrides onto components).
  const allViewComponents = useViewComponents({ workflowTemplate, viewTemplate, isReadOnly });
  const effectiveComponents = useMemo(
    () => allViewComponents.filter(isSubformWorkflowComponent),
    [allViewComponents],
  );

  const { errors, isValid } = useFormErrors({
    fields,
    components: effectiveComponents,
    apiErrors,
    includeOnSubmitErrors: submitAttempted,
    includeLocalErrors: true,
  });

  const updateField = useCallback(
    (componentId: string, fieldOrUpdater: Parameters<UpdateField>[1]) => {
      if (typeof fieldOrUpdater === 'function') {
        const updater = fieldOrUpdater as GetUpdatedField;
        setFields(prev => ({ ...prev, [componentId]: updater(prev[componentId] ?? null) }));
      } else {
        setFields(prev => ({ ...prev, [componentId]: fieldOrUpdater }));
      }
    },
    [],
  );

  const layoutEntries = useMemo(
    () => buildSubformLayoutEntries(effectiveComponents, layout),
    [effectiveComponents, layout],
  );

  const orderedComponents = useMemo(
    () => layoutEntries.filter(isSubformComponentEntry).map(entry => entry.component),
    [layoutEntries],
  );

  return {
    workflowTemplate,
    viewTemplate,
    components: effectiveComponents,
    layoutEntries,
    orderedComponents,
    fields,
    updateField,
    errors,
    isValid,
    isInitialized,
    submitAttempted,
    setSubmitAttempted,
  };
}

function initFieldRecord(
  components: API.AnyComponent[],
  initialData?: Record<string, SupportedSubformComponentValue>,
): FieldRecord {
  const record: FieldRecord = {};
  for (const component of components) {
    const raw = applyDefaultValue(component, initialData?.[component.id]);
    record[component.id] = fieldFromComponentData(component, raw);
  }
  return record;
}

function applyDefaultValue(
  component: API.AnyComponent,
  initialDataValue?: SupportedSubformComponentValue,
): SupportedSubformComponentValue | undefined {
  if (isTextComponent(component) && isUndefined(initialDataValue)) {
    return component.default?.text;
  }
  return initialDataValue;
}
